import { useCustomForm } from "@/hooks/useCustomForm"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import * as React from "react"
import {
  Grid,
  Button,
  Box,
  Typography,
  FormControl,
  TextField,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "@/lib/hooks"
import { setAuth } from "@/redux/slices/authSlice"
import { User } from "@/redux/model"

interface ResponseData {
  token: string
  user: User
  weightable: string | null
}

const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string(),
})

type LoginFormValue = z.infer<typeof LoginSchema>

const SignInForm: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { api, register, handleSubmit, errors, isSubmitting } =
    useCustomForm<LoginFormValue>({
      resolver: zodResolver(LoginSchema),
    })

  const onSubmit = async (data: LoginFormValue) => {
    const response = await api<ResponseData>("login", {
      method: "POST",
      body: JSON.stringify(data),
    })

    dispatch(setAuth(response.data))
    navigate("/")
  }

  return (
    <Box
      className="auth-main-wrapper sign-in-area"
      sx={{
        py: { xs: "60px", md: "80px", lg: "100px", xl: "135px" },
        maxHeight: "100vh",
      }}
    >
      <Box
        sx={{
          maxWidth: { sm: "500px", md: "1255px" },
          mx: "auto !important",
          px: "12px",
        }}
      >
        <Grid
          container
          alignItems="center"
          columnSpacing={{ xs: 1, sm: 2, md: 4, lg: 3 }}
        >
          <Grid item xs={12} md={6} lg={6} xl={7}>
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                maxHeight: "500px",
              }}
            >
              <img
                src="/images/sign-in.jpg"
                alt="sign-in-image"
                style={{
                  height: "500px",
                  width: "100%",
                  objectFit: "cover",
                  borderRadius: "24px",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={6} xl={5}>
            <Box
              className="form-content"
              sx={{
                paddingLeft: { xs: "0", lg: "10px" },
              }}
            >
              {/* <Box
                className="logo"
                sx={{
                  mb: "23px",
                }}
              >
                <img
                  src="/images/logo-big.svg"
                  alt="logo"
                  width={142}
                  height={38}
                />
                <img
                  src="/images/white-logo.svg"
                  className="d-none"
                  alt="logo"
                  width={142}
                  height={38}
                />
              </Box> */}

              <Box
                className="title"
                sx={{
                  mb: "23px",
                }}
              >
                <Typography
                  variant="h1"
                  className="text-black"
                  sx={{
                    fontSize: {
                      xs: "22px",
                      sm: "25px",
                      lg: "28px",
                    },
                    mb: "7px",
                    fontWeight: "600",
                  }}
                >
                  Welcome back to ANT TECH!
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Box mb="15px">
                  <FormControl fullWidth>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        mb: "10px",
                        display: "block",
                      }}
                      className="text-black"
                    >
                      Email Address
                    </Typography>

                    <TextField
                      {...register("email")}
                      label="abc&#64;gmail.com"
                      variant="filled"
                      sx={{
                        "& .MuiInputBase-root": {
                          border: "1px solid #D5D9E2",
                          backgroundColor: "#fff",
                          borderRadius: "7px",
                        },
                        "& .MuiInputBase-root::before": {
                          border: "none",
                        },
                        "& .MuiInputBase-root:hover::before": {
                          border: "none",
                        },
                      }}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  </FormControl>
                </Box>

                <Box mb="15px">
                  <FormControl fullWidth>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        mb: "10px",
                        display: "block",
                      }}
                      className="text-black"
                    >
                      Password
                    </Typography>

                    <TextField
                      {...register("password")}
                      label="Type Password"
                      variant="filled"
                      type="password"
                      id="password"
                      name="password"
                      sx={{
                        "& .MuiInputBase-root": {
                          border: "1px solid #D5D9E2",
                          backgroundColor: "#fff",
                          borderRadius: "7px",
                        },
                        "& .MuiInputBase-root::before": {
                          border: "none",
                        },
                        "& .MuiInputBase-root:hover::before": {
                          border: "none",
                        },
                      }}
                    />
                  </FormControl>
                </Box>

                {/* <Box mb="20px">
                  <Link
                    to="/authentication/forgot-password/"
                    className="text-primary"
                    style={{
                      fontWeight: "500",
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box> */}

                <Box mb="20px">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      textTransform: "capitalize",
                      borderRadius: "6px",
                      fontWeight: "500",
                      fontSize: {
                        xs: "13px",
                        sm: "16px",
                      },
                      padding: {
                        xs: "10px 20px",
                        sm: "10px 24px",
                      },
                      color: "#fff !important",
                      boxShadow: "none",
                      width: "100%",
                    }}
                  >
                    <i className="material-symbols-outlined mr-5">login</i>
                    Sign In
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default SignInForm
